/**
 * MFA enable/disable component for the Profile security tab.
 * Handles QR code display, verification, recovery code display, and disabling.
 */

import { useState } from 'react';
import { Shield, ShieldCheck, ShieldOff, Copy, Download } from 'lucide-react';
import { setupMfa, enableMfa, disableMfa } from '@/api/auth';

interface MfaSetupProps {
  mfaEnabled: boolean;
  onMfaChange: (enabled: boolean) => void;
}

type Step = 'idle' | 'qr' | 'recovery' | 'disable';

export default function MfaSetup({ mfaEnabled, onMfaChange }: MfaSetupProps) {
  const [step, setStep] = useState<Step>('idle');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [qrCode,setQrCode]=useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function handleStartSetup() {
    setError('');
    setIsLoading(true);
    try {
      const { provisioning_uri,qr_code_data_url } = await setupMfa();
      setProvisioningUri(provisioning_uri);
      setQrCode(qr_code_data_url??'');
      setStep('qr');
    } catch {
      setError('Failed to start MFA setup.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerify() {
    setError('');
    if (verifyCode.length !== 6) {
      setError('Enter a 6-digit code from your authenticator app.');
      return;
    }
    setIsLoading(true);
    try {
      const { recovery_codes } = await enableMfa(verifyCode);
      setRecoveryCodes(recovery_codes);
      setStep('recovery');
      onMfaChange(true);
    } catch {
      setError('Invalid code. Make sure you scanned the QR code and entered the current code.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDisable() {
    setError('');
    if (!disablePassword) {
      setError('Password is required.');
      return;
    }
    setIsLoading(true);
    try {
      await disableMfa(disablePassword);
      onMfaChange(false);
      setStep('idle');
      setDisablePassword('');
    } catch {
      setError('Failed to disable MFA. Check your password.');
    } finally {
      setIsLoading(false);
    }
  }

  function copyRecoveryCodes() {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
  }

  function downloadRecoveryCodes() {
    const text = `Vantage by Velosynt MFA Recovery Codes\n${'='.repeat(40)}\n\n${recoveryCodes.join('\n')}\n\nEach code can only be used once.\nStore these in a safe place.`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vantage-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  // --- MFA Enabled: show status + disable option ---
  if (mfaEnabled && step !== 'disable' && step !== 'recovery') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
          <ShieldCheck className="h-6 w-6 text-green-600" />
          <div>
            <p className="text-sm font-medium text-green-800">Two-factor authentication is enabled</p>
            <p className="text-xs text-green-600">Your account is protected with TOTP-based MFA.</p>
          </div>
        </div>
        <button
          onClick={() => setStep('disable')}
          className="btn-danger text-sm"
        >
          Disable MFA
        </button>
      </div>
    );
  }

  // --- Disable MFA form ---
  if (step === 'disable') {
    return (
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-700">Disable Two-Factor Authentication</h3>
        {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        <p className="text-sm text-gray-600">Enter your password to disable MFA. This will make your account less secure.</p>
        <input
          type="password"
          className="input-field"
          placeholder="Your password"
          value={disablePassword}
          onChange={(e) => setDisablePassword(e.target.value)}
        />
        <div className="flex gap-3">
          <button onClick={() => { setStep('idle'); setError(''); }} className="btn-secondary">Cancel</button>
          <button onClick={handleDisable} disabled={isLoading} className="btn-danger">
            {isLoading ? 'Disabling...' : 'Disable MFA'}
          </button>
        </div>
      </div>
    );
  }

  // --- Recovery codes display ---
  if (step === 'recovery') {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-800">Save your recovery codes</p>
          <p className="text-xs text-amber-600">
            These codes can be used to access your account if you lose your authenticator device.
            Each code can only be used once. Store them securely.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 rounded-md border bg-gray-50 p-4 font-mono text-sm">
          {recoveryCodes.map((code) => (
            <div key={code} className="text-center text-gray-700">{code}</div>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={copyRecoveryCodes} className="btn-secondary flex items-center gap-1.5 text-sm">
            <Copy className="h-4 w-4" /> Copy All
          </button>
          <button onClick={downloadRecoveryCodes} className="btn-secondary flex items-center gap-1.5 text-sm">
            <Download className="h-4 w-4" /> Download
          </button>
        </div>
        <button onClick={() => setStep('idle')} className="btn-primary w-full">
          I've saved my recovery codes
        </button>
      </div>
    );
  }

  // --- QR code + verification ---
  if (step === 'qr') {

    return (
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-700">Set up Two-Factor Authentication</h3>
        {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            1. Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </p>
          <div className="flex justify-center">
            {qrCode?<img src={qrCode} alt="MFA QR Code" className="h-48 w-48 rounded-md border" />:<code className="break-all">{provisioningUri}</code>}
          </div>
          <p className="text-sm text-gray-600">
            2. Enter the 6-digit code from your authenticator app to verify setup
          </p>
          <input
            className="input-field text-center text-lg tracking-widest font-mono"
            placeholder="000000"
            maxLength={6}
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
        </div>

        <div className="flex gap-3">
          <button onClick={() => { setStep('idle'); setError(''); }} className="btn-secondary">Cancel</button>
          <button onClick={handleVerify} disabled={isLoading} className="btn-primary">
            {isLoading ? 'Verifying...' : 'Verify & Enable'}
          </button>
        </div>
      </div>
    );
  }

  // --- Idle: MFA not enabled ---
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <ShieldOff className="h-6 w-6 text-gray-400" />
        <div>
          <p className="text-sm font-medium text-gray-700">Two-factor authentication is not enabled</p>
          <p className="text-xs text-gray-500">Add an extra layer of security to your account.</p>
        </div>
      </div>
      {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      <button onClick={handleStartSetup} disabled={isLoading} className="btn-primary flex items-center gap-2">
        <Shield className="h-4 w-4" />
        {isLoading ? 'Setting up...' : 'Enable Two-Factor Authentication'}
      </button>
    </div>
  );
}
